import * as tslib_1 from "tslib";
import { MediaMatcher } from '@angular/cdk/layout';
import { DOCUMENT } from '@angular/common';
import { Logger } from '@services/logger/abstract.logger';
import { StorageService } from '@services/storage.service';
import { hasSupportForPrefersColorScheme } from '@uipath/angular/utilities';
import { ExtendedUserStateManager } from 'main/app-state/generics/user-state-manager';
import { BehaviorSubject, fromEvent, Observable, of, Subscription, } from 'rxjs';
import { filter, pairwise, } from 'rxjs/operators';
import { Theme, } from './theme.types';
var THEME_KEY = 'uipath-theme';
/**
 * The current state is the theme that the user selected (auto, light, or dark).
 * ThemeManager handles the theme from the moment it's instantiated, which also includes login/register/etc. forms.
 */
var ThemeManager = /** @class */ (function (_super) {
    tslib_1.__extends(ThemeManager, _super);
    function ThemeManager(injector, _user) {
        var _this = _super.call(this, _user) || this;
        _this._user = _user;
        _this._document = injector.get(DOCUMENT);
        _this._storage = injector.get(StorageService);
        var mediaMatcher = injector.get(MediaMatcher);
        _this._state = new BehaviorSubject(_this._storageTheme);
        // The Injector doesn't work directly with abstract classes, so manual casting to Type<Logger> has to be done.
        // More details: https://github.com/angular/angular/issues/23611
        _this._logStateChanges('Theme', injector.get(Logger));
        _this._state
            .subscribe(function (theme) {
            var appliedTheme = theme === Theme.auto ? _this._determineAutoTheme() : theme;
            _this._storage.set(_this._themeKey, theme);
            _this._applyTheme(appliedTheme);
        });
        _this._state
            .pipe(pairwise(), filter(function (_a) {
            var previousTheme = _a[0], currentTheme = _a[1];
            return previousTheme !== currentTheme;
        }))
            .subscribe(function (_a) {
            var previousTheme = _a[0], currentTheme = _a[1];
            if (previousTheme === Theme.auto) {
                return _this._disposePrefersColorSchemeWatcher();
            }
            if (currentTheme === Theme.auto) {
                return _this._setPrefersColorSchemeWatcher();
            }
        });
        _this._mediaPrefersDark = mediaMatcher.matchMedia('(prefers-color-scheme: dark)');
        if (_this.value === Theme.auto) {
            _this._setPrefersColorSchemeWatcher();
        }
        return _this;
    }
    Object.defineProperty(ThemeManager.prototype, "renderedTheme", {
        get: function () {
            return this._renderedTheme;
        },
        enumerable: true,
        configurable: true
    });
    ThemeManager.prototype.set = function (newTheme) {
        this._state.next(newTheme);
    };
    ThemeManager.prototype.fetch = function () {
        var _this = this;
        return new Observable(function (subscriber) {
            _this._state.next(_this._storageTheme);
            subscriber.next(_this._storageTheme);
            subscriber.complete();
        });
    };
    ThemeManager.prototype.clean = function () {
        return of(null);
    };
    Object.defineProperty(ThemeManager.prototype, "_themeKey", {
        get: function () {
            return this._user.value ?
                THEME_KEY + "-" + this._user.value.tenancyId + "-" + this._user.value.id :
                THEME_KEY;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ThemeManager.prototype, "_storageTheme", {
        get: function () {
            return this._storage.get(this._themeKey, hasSupportForPrefersColorScheme() ? Theme.auto : Theme.light);
        },
        enumerable: true,
        configurable: true
    });
    ThemeManager.prototype._applyTheme = function (theme) {
        var remove = theme === Theme.dark ?
            Theme.light :
            Theme.dark;
        this._renderedTheme = theme;
        this._document.body.classList.remove(remove);
        this._document.body.classList.add(theme);
        this._document.body.dataset['theme'] = theme;
    };
    ThemeManager.prototype._determineAutoTheme = function () {
        if (hasSupportForPrefersColorScheme()) {
            return window.matchMedia('(prefers-color-scheme: dark)').matches ?
                Theme.dark : Theme.light;
        }
        return Theme.light;
    };
    ThemeManager.prototype._setPrefersColorSchemeWatcher = function () {
        var _this = this;
        if (this._mediaPrefersDarkSub instanceof Subscription &&
            !this._mediaPrefersDarkSub.closed) {
            return;
        }
        this._mediaPrefersDarkSub =
            fromEvent(this._mediaPrefersDark, 'change')
                .subscribe(function () {
                if (_this.value === Theme.auto) {
                    _this._applyTheme(_this._determineAutoTheme());
                }
            });
    };
    ThemeManager.prototype._disposePrefersColorSchemeWatcher = function () {
        if (this._mediaPrefersDarkSub instanceof Subscription &&
            !this._mediaPrefersDarkSub.closed) {
            this._mediaPrefersDarkSub.unsubscribe();
        }
    };
    return ThemeManager;
}(ExtendedUserStateManager));
export { ThemeManager };
